import React, { useEffect, useId, useRef, useState } from 'react';
import { Bell, ChevronDown, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function FleetAnnouncement({ tips, firstTip, fleetName }) {
  const { t } = useTranslation();
  const [displayMode, setDisplayMode] = useState(null);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const shownMessages = useRef(new Set());
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const panelId = useId();
  const previewId = useId();
  const open = displayMode !== null;
  const preview = tips.find(tip => tip?.trim()) || firstTip || '';
  const messageKey = JSON.stringify([...tips, firstTip].filter(Boolean));

  // Each distinct announcement gets one automatic preview during this page visit.
  useEffect(() => {
    if (messageKey === '[]' || shownMessages.current.has(messageKey)) return;
    shownMessages.current.add(messageKey);
    setDisplayMode('auto');
  }, [messageKey]);

  useEffect(() => {
    if (displayMode !== 'auto' || hovered || focused) return;
    const readingTime = Math.min(20000, Math.max(8000, messageKey.length * 100));
    const timer = window.setTimeout(() => setDisplayMode(null), readingTime);
    return () => window.clearTimeout(timer);
  }, [displayMode, hovered, focused, messageKey]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = event => {
      if (!rootRef.current?.contains(event.target)) setDisplayMode(null);
    };
    const onKeyDown = event => {
      if (event.key === 'Escape') {
        setDisplayMode(null);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className="fleet-announcement-widget" ref={rootRef}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={event => { if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false); }}>
      <button type="button" ref={triggerRef} className="fleet-announcement-trigger" aria-label={t('fleetDesign.announcements')} aria-describedby={preview ? previewId : undefined}
        aria-expanded={open} aria-controls={panelId} onClick={() => setDisplayMode(value => value ? null : 'manual')}>
        <Bell size={16} aria-hidden="true" />
        <span className="fleet-announcement-label">{t('fleetDesign.announcements')}</span>
        {preview && <span id={previewId} className="fleet-announcement-preview">{preview}</span>}
        <ChevronDown size={13} className="fleet-announcement-chevron" aria-hidden="true" />
      </button>
      <section id={panelId} hidden={!open} className="fleet-announcement-popover" aria-label={t('fleetDesign.announcements')}>
        <div className="fleet-announcement-heading">
          <div className="fleet-announcement-title">
            <span className="fleet-announcement-icon"><Bell size={18} aria-hidden="true" /></span>
            <div><h2>{t('fleetDesign.announcements')}</h2>{fleetName && <p>{fleetName}</p>}</div>
          </div>
          <button type="button" aria-label={t('fleetDesign.closeAnnouncements')} onClick={() => { setDisplayMode(null); triggerRef.current?.focus(); }}><X size={16} /></button>
        </div>
        <div className="fleet-announcement-content">
          {tips.map((tip, index) => <p key={index}>{tips.length > 1 ? `${index + 1}. ` : ''}{tip}</p>)}
          {firstTip && <p className="fleet-announcement-extra">{firstTip}</p>}
        </div>
      </section>
    </div>
  );
}
